// ============================================================
// StatusMessage.jsx — the admin portal's loading / error / empty row.
//
// WCAG 2.1 AA 4.1.3 (Status Messages). Before this component the admin
// portal rendered its status rows as bare divs/paragraphs:
//
//     {loading && <p className="pc-state">Loading…</p>}
//     {error   && <div className="delreq__state">{error}</div>}
//
// Visually correct, and completely silent to a screen reader: nothing
// takes focus, so unless the change is announced the user is told
// nothing at all.
//
// THE MARKUP IS BYTE-IDENTICAL TO WHAT IT REPLACES — the rendered
// element keeps each site's original tag and its EXACT className, so
// every stylesheet rule and test query keeps working. Unlike the user
// SWA's version (which derives `<family>-state <family>-state--<v>`
// from a `family` prop, because that codebase grew five parallel
// prefixes), the admin portal's status classes are heterogeneous
// one-offs (`pc-state`, `delreq__state`, `federation-tab__state`,
// `data-table__state`, …), so this version takes `className` VERBATIM
// and derives nothing. If you fix a behavioural defect here, fix the
// user-SWA sibling too (frontend/src/components/StatusMessage.jsx).
//
// Two channels, deliberately:
//   1. role/aria-live ON THIS ELEMENT — the standard, auditable
//      artefact an accessibility auditor looks for.
//   2. announce() into the persistent LiveAnnouncer region — the
//      guarantee, because a conditionally-rendered live region is
//      inserted already-populated and several SR/browser pairings do
//      not announce that (see a11y/LiveAnnouncer.jsx).
//
// The announced text is read from the rendered DOM rather than from
// the `children` prop, so composite content announces the way it
// actually reads ("Couldn't load invitations. Retry").
// ============================================================

import React, { useEffect, useRef } from 'react';
import { announce } from './a11y/LiveAnnouncer';

const ROLE = { error: 'alert', loading: 'status', empty: 'status' };
const POLITENESS = { error: 'assertive', loading: 'polite', empty: 'polite' };

export default function StatusMessage({
  variant = 'empty',
  className = '',
  // Keep each swept site's original tag so the change stays purely
  // semantic (some rows shipped as <p>, some as <div>, some as <span>).
  as: Tag = 'div',
  children,
  // Override the announced string when the visible text is not the
  // clearest thing to say (rare — prefer fixing the visible text).
  announceText,
  // Opt out where a message is decorative or duplicates an adjacent
  // announcement. Must be a deliberate choice, never a default.
  silent = false,
  ...rest
}) {
  const ref = useRef(null);
  const lastAnnounced = useRef(null);

  useEffect(() => {
    if (silent) return;
    const text = announceText != null
      ? announceText
      : (ref.current ? ref.current.textContent : '');
    if (!text) return;
    const normalised = text.replace(/\s+/g, ' ').trim();
    // Announce only when the message actually changes. A parent that
    // re-renders on every keystroke must not re-announce "Loading…".
    if (!normalised || normalised === lastAnnounced.current) return;
    lastAnnounced.current = normalised;
    announce(normalised, POLITENESS[variant] || 'polite');
  });

  return (
    <Tag
      ref={ref}
      className={className || undefined}
      role={ROLE[variant] || 'status'}
      aria-live={POLITENESS[variant] === 'assertive' ? 'assertive' : 'polite'}
      {...rest}
    >
      {children}
    </Tag>
  );
}
